import type { Status } from '@artifax/shared';

/** Status → vibrant CSS colour (dots, bars, tints — non-text use). */
export const STATUS_COLOR: Record<Status, string> = {
  operational: 'var(--status-operational)',
  degraded: 'var(--status-degraded)',
  partial: 'var(--status-partial)',
  major: 'var(--status-major)',
  maintenance: 'var(--status-maintenance)',
};

/**
 * Status → accessible "ink" colour for text. Darker variants that meet WCAG
 * 2.2 AA contrast (>=4.5:1 on white); the vibrant STATUS_COLOR is reserved for
 * dots/bars where contrast rules don't apply.
 */
export const STATUS_INK: Record<Status, string> = {
  operational: 'var(--status-operational-ink)',
  degraded: 'var(--status-degraded-ink)',
  partial: 'var(--status-partial-ink)',
  major: 'var(--status-major-ink)',
  maintenance: 'var(--status-maintenance-ink)',
};

/** Tier key shown to users (the legend). */
export const TIER_KEY: { status: Status; label: string; detail: string }[] = [
  { status: 'operational', label: 'Operational', detail: 'All sites up' },
  { status: 'degraded', label: 'Degraded', detail: 'Under 5% of sites affected' },
  { status: 'partial', label: 'Partial outage', detail: '5–25% of sites affected' },
  { status: 'major', label: 'Major outage', detail: '25% or more of sites affected' },
  // `maintenance` is intentionally omitted: we pause StatusCake during
  // maintenance rather than surfacing it, and api.ts coalesces the status to
  // `operational`. Restore this row (and remove that transform) to bring
  // planned-maintenance display back.
];

export const STATUS_LABEL: Record<Status, string> = {
  operational: 'Operational',
  degraded: 'Degraded',
  partial: 'Partial outage',
  major: 'Major outage',
  maintenance: 'Maintenance',
};

/** Copy for the overall banner, keyed by overall status. */
export const OVERALL_COPY: Record<
  Status,
  { title: string; sub: string; glyph: string }
> = {
  operational: {
    title: 'All systems operational',
    sub: 'All Artifax services are running normally across US, EU and CA.',
    glyph: '✓',
  },
  degraded: {
    title: 'Degraded performance',
    sub: 'Some Artifax services are experiencing reduced performance.',
    glyph: '!',
  },
  partial: {
    title: 'Partial system outage',
    sub: 'One or more Artifax services are partially unavailable.',
    glyph: '!',
  },
  major: {
    title: 'Major system outage',
    sub: 'One or more Artifax services are currently unavailable.',
    glyph: '!',
  },
  maintenance: {
    title: 'Scheduled maintenance',
    sub: 'Planned maintenance is in progress on one or more services.',
    glyph: '✓',
  },
};

export const SERVICE_DESCRIPTIONS = {
  event: 'Venue & event management web app',
  agora: 'Public calendar and booking access',
} as const;

export const AGORA_NOTE =
  'ArtifaxAgora is a single shared service for all customers, so it is reported once rather than per region.';

export const LINE_COLOR = {
  event: '#3a6fd0',
  agora: '#0f9d8f',
} as const;
